import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnInit,
  Optional,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { CashbackFacade } from '@features/cashback/facades/cashback.facade';
import { CashbackFormData } from '@features/cashback/types';
import { TranslateService } from '@ngx-translate/core';
import { CurrencyService } from '@rd/core/services/currency/currency.service';
import { FeatureFlagService } from '@rd/core/services/feature-flag/feature-flag.service';
import { IconRegistryService } from '@rd/core/services/icon-registry/icon-registry.service';
import { LoyaltyCurrencyService } from '@rd/core/services/loyalty-currency/loyalty-currency.service';
import { NumberService } from '@rd/core/services/number/number.service';
import { CORE_AUTH_SECURE_ROUTE_DIALOG } from '@rd/core/tokens/popup.token';
import { DialogService } from '@rd/popup/services/dialog/dialog.service';
import { CashBackMembership, LoyaltyProgram } from '@rd/types';
import * as CSS from 'csstype';
import { Subject } from 'rxjs';
import { RedeemCashSettingService } from '../../services/redeem-cash-setting/redeem-cash-setting.service';

type ComponentRenderState = 'loading' | 'content';

@Component({
  selector: 'rd-rcv2-checkout-form',
  templateUrl: './checkout-form.component.html',
  styleUrls: ['./checkout-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RcV2CheckoutFormComponent implements OnInit {
  @HostBinding('class.rd-rcv2-checkout-form') componentClass = true;
  @Input() formData: CashbackFormData;
  @Input() redeemCashLoyaltyProgram: LoyaltyProgram;
  @Input() redeemCashMembership: CashBackMembership;
  @Input() disabled: boolean;
  @Input() loading: boolean;
  @Input() isLoggedIn: boolean;
  @Output() submitForm = new EventEmitter<CashbackFormData>();
  @ViewChild('expandableContent', { static: false })
  expandableContent: ElementRef;

  checkoutForm: UntypedFormGroup;
  currencyConversionLoading = false;
  expanded = false;
  isSubmitted$: Subject<boolean> = new Subject();
  readOnlyCashFormField = false;

  private coreAuthSecureRouteDialogComponentType = inject(
    CORE_AUTH_SECURE_ROUTE_DIALOG,
  );

  constructor(
    protected fb: UntypedFormBuilder,
    protected currencyService: CurrencyService,
    protected loyaltyCurrencyService: LoyaltyCurrencyService,
    protected numberService: NumberService,
    protected iconRegistryService: IconRegistryService,
    protected redeemCashSettingService: RedeemCashSettingService,
    protected dialogService: DialogService,
    protected translateService: TranslateService,
    public featureFlagService: FeatureFlagService,
    @Optional() protected router?: Router,
    @Optional() protected cashbackFacade?: CashbackFacade,
  ) {
    this.initForms();
  }

  ngOnInit(): void {
    this.registerIcons();

    if (this.formData?.sourceAmount) {
      this.expanded = true;
    }

    this.readOnlyCashFormField =
      !this.redeemCashSettingService.shouldAllowEditCashValue;
  }

  getComponentRenderState(): ComponentRenderState {
    if (this.loading) {
      return 'loading';
    } else {
      return 'content';
    }
  }

  private initForms(): void {
    this.checkoutForm = this.fb.group({
      transferAmountForm: [''],
    });
  }

  onSubmit(): void {
    // Inform child form that the parent form has been submitted, so they mark each of their components as touched
    if (!this.isLoggedIn) {
      this.dialogService.open(this.coreAuthSecureRouteDialogComponentType, {
        id: 'auth-secure-route-dialog',
        screenMode: 'center-md',
        ariaLabel: this.translateService.instant(
          'accessibility.core.auth_dialog_aria_label',
        ),
        keepModalOnRouteChange: true,
      });
      return;
    }

    this.isSubmitted$.next(true);

    if (this.checkoutForm.valid) {
      const { sourceAmount, targetAmount } =
        this.checkoutForm.controls.transferAmountForm.value;

      this.submitForm.emit({ sourceAmount, targetAmount });
    }
  }

  onAutoRedemptionSubmit(): void {
    const { sourceAmount, targetAmount } =
      this.checkoutForm.controls.transferAmountForm.value;
    this.cashbackFacade?.submitCashbackForm({
      loyaltyProgramId: this.redeemCashLoyaltyProgram?.id,
      formData: {
        sourceAmount,
        targetAmount,
      },
    });
    this.router?.navigateByUrl(
      '/redeem_cash/checkout/auto-redemption/enrollment',
    );
  }

  buttonDisabled(): boolean {
    return this.currencyConversionLoading;
  }

  toggleExpansion(): void {
    if (!this.disabled) {
      this.expanded = !this.expanded;
    }
  }

  overflowHeight(): CSS.Properties {
    if (this.expandableContent && this.expanded) {
      const allowanceInPx = 20;
      return {
        marginTop: `${allowanceInPx}px`,
        marginBottom: `${allowanceInPx}px`,
        height: 'auto',
      };
    } else {
      return {};
    }
  }

  private registerIcons(): void {
    this.iconRegistryService.registerIcon('arrow-down');
  }

  /* ==== Translation Params ===== */

  loyaltyCurrencyTranslationParams(): { loyaltyCurrency: string } {
    return {
      loyaltyCurrency:
        // @ts-ignore
        this.loyaltyCurrencyService.loyaltyCurrencySelected.shortName,
    };
  }

  calculationTextTranslationParams(): {
    cashEquivalent: string;
    minimumSourceAmountWithCurrency: string;
  } {
    const {
      // @ts-ignore
      denomination,
      // @ts-ignore
      exchangeRate,
    } = this.redeemCashLoyaltyProgram.loyaltyCurrency;
    const cashEquivalent = this.currencyService.formatCurrency(
      denomination * exchangeRate,
      'codeWithSpace',
      '1.0-0',
    );
    const selectedLoyaltyCurrency =
      // @ts-ignore
      this.loyaltyCurrencyService.loyaltyCurrencySelected.name;
    const minimumSourceAmountWithCurrency = `${this.numberService.formatNumber(
      denomination,
    )} ${selectedLoyaltyCurrency}`;

    return {
      cashEquivalent,
      minimumSourceAmountWithCurrency,
    };
  }

  setCurrencyConversionLoading(loading: boolean): void {
    this.currencyConversionLoading = loading;
  }

  /* ========= */
}
